'use client';

import { Box, SystemIcon, Typography } from '@wishket/design-system';
import { useEffect, useRef, useState } from 'react';

import { CommentTextarea } from '@/shared/Components';

import { useComment, useDebounce, useProfile } from '@/shared/hooks';
import { ArticleCommentType } from '@/shared/types';
import { convertDateString } from '@/shared/utils';
import {
  useDeleteArticleCommentApi,
  usePatchArticleCommentApi,
  usePostArticleCommentApi,
} from '@/entities/article';
import CommentItem from '@/features/CommentItem';

interface TargetComment {
  isSelected: boolean;
  open: (id: number) => void;
  close: () => void;
}

interface PageStatus {
  current: number;
  change: (id: number) => void;
}

interface ArticleCommentProps {
  newsId: string;
  newCommentId?: number;
  resetNewCommentId?: () => void;
  pageStatus: PageStatus;
  comment: ArticleCommentType;
  target: TargetComment;
  isLastOfPage: boolean;
}

const GUIDE_TEXT =
  '건전한 댓글 문화를 위해 불쾌감을 주는 욕설, 특정 대상을 비하하는 댓글은 표시가 제한됩니다.';
const DELETE_CONFIRM_TEXT = '댓글 삭제 시 이력이 남습니다. 삭제하시겠습니까?';
const DELETED_BY_MANAGER = '관리자가 삭제한 댓글입니다.';
const DELETED_BY_SELF = '작성자가 삭제한 댓글입니다.';

const ArticleComment = ({
  newsId,
  newCommentId,
  resetNewCommentId,
  pageStatus,
  comment,
  target,
  isLastOfPage,
}: ArticleCommentProps) => {
  const {
    rootId,
    depth,
    id,
    imgUrl,
    comment: content,
    dateCreated,
    dateModified,
    dateDeleted,
    username,
    parentUsername,
    isAuthor,
    isModified,
    isDeletedByManager,
    isMine,
  } = comment;
  const isEditable = isMine;
  const isReply = depth === 2;
  const isDeleted = dateDeleted !== null || isDeletedByManager;
  const isNewComment = newCommentId === id;
  const deletedText = isDeletedByManager ? DELETED_BY_MANAGER : DELETED_BY_SELF;
  const organizedTime = isModified && dateModified ? dateModified : dateCreated;

  const commentItemRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const { profile } = useProfile();

  const [editedComment, onEditedCommentChange, resetEditedComment] =
    useComment(content);
  const [replyComment, onReplyCommentChange, resetReplyComment] =
    useComment('');

  const { mutate: postComment, isPending: isPostPending } =
    usePostArticleCommentApi();
  const { mutate: updateComment } = usePatchArticleCommentApi();
  const { mutate: deleteComment } = useDeleteArticleCommentApi();

  const handleEditing = () => {
    target.open(id);
    setIsReplying(false);
    setIsEditing(true);
  };

  const handleReplying = () => {
    target.open(id);
    setIsEditing(false);
    setIsReplying((pre) => !pre);
  };

  const cancelEdit = () => {
    target.close();
    resetEditedComment();
    setIsEditing(false);
  };

  const cancelReply = () => {
    target.close();
    resetReplyComment();
    setIsReplying(false);
  };

  const handleSubmitReply = useDebounce(() => {
    const hasNothingToPost = !replyComment.trim();
    const modifiedPage = isLastOfPage
      ? pageStatus.current + 1
      : pageStatus.current;

    if (hasNothingToPost || isPostPending) return;

    postComment(
      {
        newsId,
        comment: replyComment.trim(),
        parentId: id,
        page: modifiedPage,
      },
      {
        onSuccess: () => {
          setIsReplying(false);
          resetReplyComment();
          isLastOfPage && pageStatus.change(modifiedPage);
        },
      }
    );
  });

  const onDelete = () => {
    if (confirm(DELETE_CONFIRM_TEXT))
      deleteComment({ newsId, page: pageStatus.current, commentId: id });
  };

  const handleEdit = useDebounce(() => {
    const isEditedCommentBlank = !editedComment.trim();
    const hasNoChanges = editedComment === content;

    if (isEditedCommentBlank) return;
    if (hasNoChanges) return setIsEditing(false);

    updateComment(
      {
        newsId,
        page: pageStatus.current,
        commentId: id,
        comment: editedComment,
        parentId: rootId,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          resetEditedComment();
        },
      }
    );
  });

  const generateKoreanTimeFormat = (timeString: string) => {
    const convertedTimeString = convertDateString(
      timeString,
      'yyyy.mm.dd hh:mi'
    );

    const [date, time] = convertedTimeString.split(' ');

    const [hour, minute] = time.split(':');

    const AP = Number(hour) < 12 ? '오전' : '오후';
    const organizedHour = Number(hour) - 12 > 0 ? Number(hour) - 12 : hour;

    return `${date} ${AP} ${organizedHour}:${minute}`;
  };

  useEffect(() => {
    if (isNewComment && commentItemRef.current) {
      commentItemRef.current.scrollIntoView({
        block: 'center',
      });
      resetNewCommentId?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewComment]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'center' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.hash]);

  return (
    <Box
      className="w-full"
      ref={isNewComment ? commentItemRef : undefined}
      id={`comment_${id}`}
    >
      {isDeleted ? (
        <Box className="flex w-full gap-4">
          {isReply && (
            <SystemIcon name="medium_reply" className="text-w-gray-400" />
          )}
          <Typography variant="body1" className="text-w-gray-600">
            {deletedText}
          </Typography>
        </Box>
      ) : (
        <>
          <Box>
            {target.isSelected && isEditing ? (
              <Box className="flex w-full gap-4">
                {isReply && (
                  <SystemIcon name="medium_reply" className="text-w-gray-400" />
                )}
                <CommentTextarea
                  isEdit
                  maxLength={500}
                  guideText={GUIDE_TEXT}
                  userId={username}
                  imgUrl={imgUrl}
                  value={editedComment}
                  onChange={onEditedCommentChange}
                  onSubmit={handleEdit}
                  onCancel={cancelEdit}
                  buttonName="수정하기"
                />
              </Box>
            ) : (
              <CommentItem
                isAuthor={isAuthor}
                imgUrl={imgUrl}
                username={username}
                parentUsername={parentUsername}
                comment={content}
                time={generateKoreanTimeFormat(organizedTime)}
                isReply={isReply}
                isEditable={isEditable}
                isModified={isModified}
                onEdit={handleEditing}
                onReply={handleReplying}
                onDelete={onDelete}
              />
            )}
          </Box>
          {target.isSelected && isReplying && (
            <Box className="mt-8 flex gap-4">
              <SystemIcon name="medium_reply" className="text-w-gray-400" />
              <CommentTextarea
                isEdit
                maxLength={500}
                guideText={GUIDE_TEXT}
                userId={profile?.name}
                imgUrl={profile?.imgSrc}
                value={replyComment}
                onChange={onReplyCommentChange}
                onCancel={cancelReply}
                onSubmit={handleSubmitReply}
                buttonName="작성하기"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ArticleComment;
